import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { BrgEvent } from '../types';
import { formatDateAndTime, truncateText } from '../utils';
import { getTextColor } from '../../../lib/colorUtils';

interface EventDetailModalProps {
  event: BrgEvent;
  onClose: () => void;
  onEdit?: (event: BrgEvent) => void;
  onDelete?: (eventId: number) => void;
  currentUserEmail?: string;
  isAdmin?: boolean;
}

/**
 * Event Detail Modal Component
 * Displays full event information in an overlay modal
 * Rendered as a Portal at the document root to avoid positioning context issues
 */
export default function EventDetailModal({
  event,
  onClose,
  onEdit,
  onDelete,
  currentUserEmail,
  isAdmin,
}: EventDetailModalProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Check if current user is creator or admin.
  // Some local DBs truncate createdByEmail, so allow prefix matches in either direction.
  const normalizedCurrentEmail = (currentUserEmail || '').toLowerCase();
  const normalizedCreatedBy = (event.createdBy || '').toLowerCase();
  const isOwnerByEmail =
    !!normalizedCurrentEmail
    && !!normalizedCreatedBy
    && (
      normalizedCurrentEmail === normalizedCreatedBy
      || normalizedCurrentEmail.startsWith(normalizedCreatedBy)
      || normalizedCreatedBy.startsWith(normalizedCurrentEmail)
    );

  const canEditDelete = !!isAdmin || isOwnerByEmail;

  if (!mounted) return null;

  const modalContent = (
    <>
      {/* Overlay */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          zIndex: 99999
        }}
        onClick={onClose}
      />

      {/* Modal */}
      <div 
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(calc(100% - 32px), 560px)',
          maxHeight: 'auto',
          overflow: 'hidden',
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          zIndex: 100000
        }}
      >
        {/* Header */}
        <div
          style={{ 
            backgroundColor: event.brgColor,
            padding: '16px 16px 12px 16px',
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}
        >
          <h2 
            style={{
              color: getTextColor(event.brgColor),
              fontSize: '24px',
              fontWeight: '700',
              margin: '0',
              lineHeight: '1.3',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
              flex: 1
            }}
          >
            {event.eventName}
          </h2>
          <button
            onClick={onClose}
            style={{ 
              color: getTextColor(event.brgColor),
              fontSize: '24px',
              fontWeight: 'bold',
              lineHeight: 1,
              marginTop: '0px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              flexShrink: 0,
              marginLeft: '10px'
            }}
            onMouseEnter={(e) => {
              const isDark = getTextColor(event.brgColor) === '#333333';
              e.currentTarget.style.color = isDark ? '#666666' : '#F0F0F0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = getTextColor(event.brgColor);
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div 
          style={{
            padding: '16px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", sans-serif'
          }}
        >
          {/* BRG Section */}
          <div style={{ marginBottom: '12px' }}>
            <div 
              style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#8B8B8B',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '4px'
              }}
            >
              BRG
            </div>
            <div 
              style={{
                display: 'inline-block',
                backgroundColor: event.brgColor,
                padding: '6px 12px',
                borderRadius: '16px',
                border: 'none'
              }}
            >
              <span 
                style={{
                  fontWeight: '600',
                  fontSize: '12px',
                  color: getTextColor(event.brgColor)
                }}
              >
                {event.brgName}
              </span>
            </div>
          </div>

          {/* Date & Time */}
          <div style={{ marginBottom: '12px' }}>
            <div 
              style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#8B8B8B',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '4px'
              }}
            >
              Date & Time
            </div>
            <div 
              style={{
                fontSize: '14px',
                color: '#333333',
                fontWeight: '400'
              }}
            >
              {formatDateAndTime(event.date, event.time)}
            </div>
          </div>

          {/* Organizer */}
          <div style={{ marginBottom: '12px' }}>
            <div 
              style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#8B8B8B',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '4px'
              }}
            >
              Organizer
            </div>
            <div 
              style={{
                fontSize: '14px',
                color: '#333333',
                fontWeight: '400'
              }}
            >
              {event.organizer}
            </div>
          </div>

          {/* Location */}
          <div style={{ marginBottom: '12px' }}>
            <div 
              style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#8B8B8B',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '4px'
              }}
            >
              Location
            </div>
            <div 
              style={{
                fontSize: '14px',
                color: '#333333',
                fontWeight: '400'
              }}
            >
              {event.location}
            </div>
          </div>

          {/* Accepted Attendees */}
          <div style={{ marginBottom: '12px' }}>
            <div 
              style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#8B8B8B',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '4px'
              }}
            >
              Accepted Attendees
            </div>
            <div 
              style={{
                fontSize: '14px',
                color: '#333333',
                fontWeight: '400'
              }}
            >
              {event.acceptedAttendees || 'N/A'}
            </div>
          </div>

          {/* Event Description */}
          {event.eventDescription && (
            <div>
              <div 
                style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#8B8B8B',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '4px'
                }}
              >
                Event Description
              </div>
              <div 
                style={{
                  fontSize: '14px',
                  color: '#333333',
                  fontWeight: '400',
                  lineHeight: '1.5',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  whiteSpace: 'normal',
                  maxWidth: '100%'
                }}
              >
                {truncateText(event.eventDescription, 100)}
              </div>
            </div>
          )}
        </div>

        {/* Footer with Action Buttons - Only show if user is creator or admin */}
        {canEditDelete && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              padding: '16px',
              borderTop: '1px solid #E5E7EB',
              backgroundColor: '#FAFBFC'
            }}
          >
            <button
              onClick={() => {
                if (onDelete) {
                  onDelete(event.id);
                }
                onClose();
              }}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#EF4444',
                backgroundColor: 'transparent',
                border: '1px solid #EF4444',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#FEE2E2';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Delete
            </button>
            <button
              onClick={() => {
                if (onEdit) {
                  onEdit(event);
                }
                onClose();
              }}
              style={{
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#FFFFFF',
                backgroundColor: '#1F40AF',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1A329D';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#1F40AF';
              }}
            >
              Edit
            </button>
          </div>
        )}
      </div>
    </>
  );

  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
}
